import { useNavigate } from 'react-router-dom'
import { api } from '../api/client'
import type { ImportRecord, ImportQuery } from '../api/client'
import { useHasPermission } from '../api/session'
import { useListParams } from '../hooks/useListParams'
import { useListQuery } from '../hooks/useListQuery'
import { Card } from '../components/card/Card'
import { Button } from '../components/common/Button'
import { Input, Select } from '../components/common/Controls'
import { EmptyState, ErrorBox } from '../components/common/Feedback'
import { FilterBar, FilterEnd, FilterFixed } from '../components/common/FilterBar'
import { PermissionNotice } from '../components/common/PermissionNotice'
import { DataTable } from '../components/table/DataTable'
import type { Column } from '../components/table/types'
import s from './ImportsPage.module.scss'

/**
 * Die Importstrecke — was geliefert wurde und ob es ankam.
 *
 * Drei Betriebsfragen: Ist geliefert worden? Ist es angekommen? Was liegt
 * noch offen?
 *
 * OHNE NUTZLAST. Die Liste zeigt nur deren Umfang; der Inhalt steht auf der
 * Detailseite, und auch dort nur sein Anfang.
 */

const PERMISSION = 'import:read'
const PAGE_SIZE = 50

const zeitpunkt = new Intl.DateTimeFormat('de-DE', {
  dateStyle: 'short', timeStyle: 'short',
})
const datum = new Intl.DateTimeFormat('de-DE', { dateStyle: 'short' })

/** Die Arten, die der Dienst kennt. */
const ARTEN = ['PRICE_HISTORY', 'QUOTES', 'INSTRUMENTS', 'FX_RATES']

/**
 * Bytes in etwas, das man lesen kann.
 *
 * Der Umfang ist die Ersatzgröße für die Nutzlast, die hier fehlt — 19 kB
 * gegen 1,4 MB sagt auf einen Blick, ob ein Tageslauf oder eine
 * Erstbefüllung vorliegt.
 */
function umfang(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`
  return `${(bytes / 1024 / 1024).toLocaleString('de-DE', {
    minimumFractionDigits: 1, maximumFractionDigits: 1 })} MB`
}

/**
 * Der Stand einer Lieferung — drei Zustände, mehr gibt es nicht.
 *
 * OFFEN heißt seit dem Umbau am Import-Controller wirklich offen: Vorher
 * wurde jede Lieferung als abgeleitet markiert, deren Ableitungsversuch ohne
 * Ausnahme durchlief — auch die, für die es gar keinen Ableiter gibt.
 */
function Stand({ zeile }: { zeile: ImportRecord }) {
  if (zeile.derive_error) {
    return (
      <span className={s.stand} data-ton="fehler" title={zeile.derive_error}>
        gescheitert
      </span>
    )
  }
  if (zeile.derived_at) {
    return (
      <span className={s.stand} data-ton="fertig"
            title={`übernommen am ${zeitpunkt.format(new Date(zeile.derived_at))}`}>
        übernommen
      </span>
    )
  }
  return <span className={s.stand} data-ton="offen">offen</span>
}

const COLUMNS: Column<ImportRecord>[] = [
  { key: 'created', label: 'Angelegt', sortable: true, mono: true,
    minWidth: '128px',
    render: (row) => zeitpunkt.format(new Date(row.created)),
    help: 'Wann die Lieferung ankam — nicht, für welchen Tag sie gilt. Ein Kursimport von heute kann Handelstage aus dem Jahr 2000 enthalten.' },
  { key: 'data_type', label: 'Art', sortable: true, minWidth: '140px',
    render: (row) => <span className={s.art}>{row.data_type}</span>,
    help: 'Was der Dienst daraus macht. PRICE_HISTORY und FX_RATES werden abgeleitet, QUOTES und INSTRUMENTS liegen als Beleg.' },
  { key: 'source', label: 'Quelle', sortable: true, minWidth: '130px',
    help: 'Wer geliefert hat. Der Collector trägt hier den Lieferanten ein, nicht sich selbst.' },
  { key: 'reference', label: 'Referenz', minWidth: '250px',
    render: (row) => row.reference
      ? <span className={s.referenz}>{row.reference}</span>
      : <span className={s.ohne}>—</span>,
    copy: (row) => row.reference ?? '',
    help: 'Die Kennung des Laufs, vom Lieferanten vergeben. Sie ist eindeutig — eine zweimal gesendete Lieferung wird damit abgewiesen. Nicht jeder Lieferant vergibt eine.' },
  { key: 'payload_bytes', label: 'Umfang', sortable: false, mono: true,
    align: 'right', minWidth: '90px',
    render: (row) => umfang(row.payload_bytes),
    help: 'Wie groß die Rohlieferung ist. Sie selbst wird hier NICHT geladen — eine Seite mit fünfzig Lieferungen zöge sonst leicht siebzig Megabyte. Der Inhalt steht auf der Detailseite.' },
  { key: 'derived_at', label: 'Stand', minWidth: '120px',
    render: (row) => <Stand zeile={row} />,
    help: 'Übernommen heißt: in die Zieltabellen geschrieben. Offen heißt: noch nicht — entweder gibt es für diese Art keinen Ableiter, oder er ist nicht gelaufen. Gescheitert nennt beim Überfahren den Grund.' },
  { key: 'delete_at', label: 'Aufgehoben bis', mono: true, align: 'right',
    minWidth: '130px', hideOn: 'tablet',
    render: (row) => row.delete_at
      ? datum.format(new Date(row.delete_at))
      : <span className={s.ohne}>unbefristet</span>,
    help: 'Ab wann die Rohlieferung gelöscht werden DARF — gelöscht wird bislang nichts. Abgeleitetes bekommt 90 Tage, Belege wie Kurse und Stammdaten bleiben.' },
]

export function ImportsPage() {
  const allowed = useHasPermission(PERMISSION)
  const navigate = useNavigate()

  const list = useListParams({
    filters: { art: '', von: '', bis: '' },
    sort: 'created',
    // Die jüngste Lieferung zuerst — die Frage ist fast immer "ist der
    // letzte Lauf durchgekommen".
    descending: true,
  })

  const params: ImportQuery = {
    data_type: list.filters.art || undefined,
    von: list.filters.von ? `${list.filters.von}T00:00:00Z` : undefined,
    bis: list.filters.bis ? `${list.filters.bis}T23:59:59Z` : undefined,
    sort: list.sort as ImportQuery['sort'],
    dir: list.descending ? 'desc' : 'asc',
    page: list.page,
    size: PAGE_SIZE,
    total: true,
  }

  const { result, loading, error, denied } = useListQuery(
    api.imports, params, allowed)

  if (!allowed || denied) {
    return <PermissionNotice permission={PERMISSION}
                             subject="die Importstrecke" />
  }

  return (
    <>
      <FilterBar>
        <FilterFixed>
          <Select label="Art" value={list.filters.art}
                  onChange={(event) => list.setFilter('art', event.target.value)}>
            <option value="">alle Arten</option>
            {ARTEN.map((art) => <option key={art} value={art}>{art}</option>)}
          </Select>
        </FilterFixed>
        <FilterFixed>
          <Input label="Von" type="date" value={list.filters.von}
                 onChange={(event) => list.setFilter('von', event.target.value)} />
        </FilterFixed>
        <FilterFixed>
          <Input label="Bis" type="date" value={list.filters.bis}
                 onChange={(event) => list.setFilter('bis', event.target.value)} />
        </FilterFixed>
        <FilterEnd>
          <Button onClick={list.reset} disabled={!list.touched}>
            Zurücksetzen
          </Button>
        </FilterEnd>
      </FilterBar>

      {error && <ErrorBox>{error}</ErrorBox>}

      <Card flush span={12}>
        <DataTable
          columns={COLUMNS}
          rows={result?.items ?? []}
          rowKey={(row) => row.id}
          sort={{ key: list.sort, descending: list.descending }}
          onSort={(next) => list.setSort(next.key, next.descending)}
          page={result ? { page: result.page, size: result.size,
                           total: result.total, hasMore: result.has_more }
                       : undefined}
          onPage={list.setPage}
          onRowClick={(row) => navigate(`/admin/imports/${row.id}`)}
          loading={loading && result === null}
          empty={<EmptyState title="keine Lieferungen">
            {list.touched
              ? 'Keine Lieferung passt zu diesen Filtern.'
              : 'Es ist noch nichts geliefert worden.'}
          </EmptyState>}
        />
      </Card>
    </>
  )
}
